import React from "react";
import "react-photo-view/dist/react-photo-view.css";
import "./i18n"; // Initialize i18n
import { Route,
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
} from "react-router-dom";
import {
  SimpleErrorBoundary,
  MainLayout,
  MainLayoutLoader,
  MainLayoutErrorBoundary,
  requireAuthenticatedLoader,
  AuthenticatedRouteOutlet,
} from "./components";
import {
  HomePage,
  CalendarPage,
  MessagesPage,
  SettingsPage,
  HelpPage,
  AccountPage,
} from "./pages";
import { RouterFallback } from "./components/Layout/Fallbacks";
import { homeRouteHandle } from "./components/Layout/Fallbacks/routeContentFallbackRegistry";
import { LandingPageLoader } from "./pages/LandingPage/loaders";
import {
  HomePageAction,
  HomePageLoader,
} from "./pages/HomePage/loaders/HomePageLoader";
import { SignUpPageAction, SignUpPageLoader } from "./pages/SignUpPage/loaders";
import { HelpPageLoader } from "./pages/HelpPage/loaders";
import { MessagePageLoader } from "./pages/MessagesPage/loaders";
import { AccountPageLoader, AccountPageAction } from "./pages/AccountPage/loaders";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./lib/queryClient";
import { mainLayoutShouldRevalidate } from "./components/Layout/MainLayout/loader/mainLayoutShouldRevalidate";
import {
  UnitPagesPublicRoutes,
  UnitPagesProtectedRoutes,
} from "./pages/UnitPages/routes";
import { AmenityPagesRoutes } from "./pages/AmenityPages/routes";
import { UnitPlanPagesRoutes } from "./pages/UnitPlanPages/routes";
import { TenantPagesRoutes } from "./pages/TenantsPages/routes";
import { OrganizationPagesRoutes } from "./pages/OrganizationPages/routes";
import {
  BuildingPagesPublicRoutes,
  BuildingPagesProtectedRoutes,
} from "./pages/BuildingPages/routes";
import { ParcelPagesProtectedRoutes } from "./pages/ParcelPages/routes";
import {
  PropertyPagesPublicRoutes,
  PropertyPagesProtectedRoutes,
} from "./pages/PropertyPages/routes";
import { FinancePagesRoutes } from "./pages/FinancePages/routes";
import "@cyntler/react-doc-viewer/dist/index.css";
import { ServiceRequestRoutes } from "./pages/ServiceRequestPages/routes";
import { LeasePagesRoutes } from "./pages/LeasePages/routes";
import { StayPagesRoutes } from "./pages/StayPages/routes";
import { TransactionTableRoutes } from "./pages/TransactionTablePage/routes";
import {
  PublicShellLayout,
  SignUpPageContent,
} from "./components/Layout/PublicShell";
import { LandingPage } from "./pages";

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route>
      <Route element={<PublicShellLayout />}>
        <Route
          path="/explore"
          loader={LandingPageLoader}
          element={<LandingPage />}
        />
        <Route
          path="/signup"
          loader={SignUpPageLoader}
          action={SignUpPageAction}
          ErrorBoundary={SimpleErrorBoundary}
          element={<SignUpPageContent />}
        />
      </Route>

      <Route
        id="main"
        element={<MainLayout />}
        loader={MainLayoutLoader}
        shouldRevalidate={mainLayoutShouldRevalidate}
        ErrorBoundary={() => <MainLayoutErrorBoundary />}
      >
        <Route
          path="/"
          loader={HomePageLoader}
          action={HomePageAction}
          element={<HomePage />}
          handle={homeRouteHandle}
        />

        {PropertyPagesPublicRoutes()}
        {BuildingPagesPublicRoutes()}
        {UnitPagesPublicRoutes()}

        <Route
          loader={requireAuthenticatedLoader}
          element={<AuthenticatedRouteOutlet />}
        >
          {PropertyPagesProtectedRoutes()}
          {BuildingPagesProtectedRoutes()}
          {ParcelPagesProtectedRoutes()}
          {UnitPagesProtectedRoutes()}
          {AmenityPagesRoutes()}
          {UnitPlanPagesRoutes()}
          {TenantPagesRoutes()}
          {LeasePagesRoutes()}
          {StayPagesRoutes()}
          {OrganizationPagesRoutes()}
          {FinancePagesRoutes()}
          {ServiceRequestRoutes()}
          {TransactionTableRoutes()}

          <Route path="/calendar" element={<CalendarPage />} />

          <Route
            path="/messages"
            loader={MessagePageLoader}
            element={<MessagesPage />}
          />

          <Route
            path="/account"
            loader={AccountPageLoader}
            action={AccountPageAction}
            element={<AccountPage />}
          />

          <Route path="/settings" element={<SettingsPage />} />

          <Route path="/help" loader={HelpPageLoader} element={<HelpPage />} />
        </Route>
      </Route>
    </Route>
  )
);

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} fallbackElement={<RouterFallback />} />
    </QueryClientProvider>
  );
};

export default App;
